import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { jobsService } from '../services/api';
import { ErrorBanner } from './ErrorMessage';
import { getErrorMessage } from '../utils/errors';
import { formatTime, formatShortDateTime } from '../utils/format';
import { EMPTY_FILTERS, matchesJob, buildFilterOptions } from '../utils/jobFilters';

const PAGE_SIZE = 50;
const HOURS_OPTIONS = [
    { value: 24, label: 'Last 24 hours' },
    { value: 48, label: 'Last 48 hours' },
    { value: 168, label: 'Last 7 days' }
];
const DEFAULT_HOURS = 48;

// Filters live in the URL so a filtered view can be bookmarked or shared.
// `service` is accepted as an alias for `server` (used by Command Center links).
const readFilters = (params) => ({
    q: params.get('q') || '',
    server: params.get('server') || params.get('service') || '',
    client: params.get('client') || '',
    fileType: params.get('type') || '',
    status: params.get('status') || '',
    errorsOnly: params.get('errors') === '1'
});

const writeFilters = (filters, hours) => {
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.server) params.server = filters.server;
    if (filters.client) params.client = filters.client;
    if (filters.fileType) params.type = filters.fileType;
    if (filters.status) params.status = filters.status;
    if (filters.errorsOnly) params.errors = '1';
    if (hours !== DEFAULT_HOURS) params.hours = String(hours);
    return params;
};

function Jobs() {
    const [activeJobs, setActiveJobs] = useState([]);
    const [recentJobs, setRecentJobs] = useState([]);
    const [expandedJobs, setExpandedJobs] = useState({});
    const [jobDetails, setJobDetails] = useState({});
    const [refreshInterval, setRefreshInterval] = useState(10);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = readFilters(searchParams);
    const hours = Number(searchParams.get('hours')) || DEFAULT_HOURS;
    const [activeLimit, setActiveLimit] = useState(PAGE_SIZE);
    const [recentLimit, setRecentLimit] = useState(PAGE_SIZE);

    const updateFilters = (changes, newHours = hours) => {
        setSearchParams(writeFilters({ ...filters, ...changes }, newHours), { replace: true });
        setActiveLimit(PAGE_SIZE);
        setRecentLimit(PAGE_SIZE);
    };
    const clearFilters = () => updateFilters(EMPTY_FILTERS);
    const hasFilters = Object.keys(EMPTY_FILTERS).some(k => filters[k] !== EMPTY_FILTERS[k]);

    const options = useMemo(() => {
        const opts = buildFilterOptions([...activeJobs, ...recentJobs]);
        // Keep a server chosen via a Command Center link selectable even if it has no jobs
        if (filters.server && !opts.servers.includes(filters.server)) opts.servers.unshift(filters.server);
        return opts;
    }, [activeJobs, recentJobs, filters.server]);

    const filteredActive = useMemo(() => activeJobs.filter(j => matchesJob(j, filters)), [activeJobs, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
    const filteredRecent = useMemo(() => recentJobs.filter(j => matchesJob(j, filters)), [recentJobs, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    // The interval always calls the latest loadData, so it sees the current hours and expanded jobs
    const loadDataRef = useRef();

    useEffect(() => {
        loadDataRef.current();
    }, [hours]);

    // Auto-refresh
    useEffect(() => {
        if (refreshInterval === 0) return;
        const interval = setInterval(() => loadDataRef.current(), refreshInterval * 1000);
        return () => clearInterval(interval);
    }, [refreshInterval]);

    const loadData = async () => {
        try {
            const [active, recent] = await Promise.all([
                jobsService.getActiveJobs(),
                jobsService.getJobs(hours)
            ]);
            setActiveJobs(active || []);
            setRecentJobs((recent || []).filter(j => j.status !== 'Queued' && j.status !== 'Running'));
            setError(null);

            // Refresh expanded job details
            for (const jobId of Object.keys(expandedJobs)) {
                if (expandedJobs[jobId]) {
                    const detail = await jobsService.getJobDetail(jobId);
                    setJobDetails(prev => ({ ...prev, [jobId]: detail }));
                }
            }
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };
    loadDataRef.current = loadData;

    const toggleJobExpanded = async (jobId) => {
        const isExpanded = expandedJobs[jobId];
        setExpandedJobs(prev => ({ ...prev, [jobId]: !isExpanded }));
        
        if (!isExpanded && (!jobDetails[jobId] || jobDetails[jobId].error)) {
            try {
                const detail = await jobsService.getJobDetail(jobId);
                setJobDetails(prev => ({ ...prev, [jobId]: detail }));
            } catch (err) {
                setJobDetails(prev => ({ ...prev, [jobId]: { error: getErrorMessage(err) } }));
            }
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Complete': return '✅';
            case 'Running': return '🔵';
            case 'Failed': return '❌';
            case 'Skipped': return '⏭️';
            case 'Pending': case 'Queued': return '⏳';
            default: return '⚪';
        }
    };

    const getStepStatusIcon = (status) => {
        switch (status) {
            case 'Complete': return '✅';
            case 'Running': return '🔵';
            case 'Failed': return '❌';
            case 'Skipped': return '⏭️';
            default: return '⏳';
        }
    };

    if (loading) {
        return (
            <div className="jobs-page">
                <div className="jobs-loading">
                    <div className="spinner"></div>
                    <div>Loading jobs...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="jobs-page">
            <header className="jobs-header">
                <div className="jobs-header-left">
                    <h1>Jobs & Tasks</h1>
                </div>
                <div className="jobs-header-right">
                    <div className="jobs-refresh-control">
                        <label>Refresh:</label>
                        <select value={refreshInterval} onChange={(e) => setRefreshInterval(Number(e.target.value))}>
                            <option value={0}>Off</option>
                            <option value={10}>10s</option>
                            <option value={30}>30s</option>
                            <option value={60}>1m</option>
                        </select>
                    </div>
                </div>
            </header>

            <ErrorBanner
                message={error && `Couldn't refresh jobs: ${error}`}
                onRetry={loadData}
            />

            <div className="jobs-filters">
                <input
                    type="search"
                    className="jobs-filter-search"
                    placeholder="Search job ID, name, client, file, error..."
                    value={filters.q}
                    onChange={(e) => updateFilters({ q: e.target.value })}
                />
                <select value={filters.server} onChange={(e) => updateFilters({ server: e.target.value })} aria-label="Server">
                    <option value="">All servers</option>
                    {options.servers.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={filters.client} onChange={(e) => updateFilters({ client: e.target.value })} aria-label="Client">
                    <option value="">All clients</option>
                    {options.clients.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <select value={filters.fileType} onChange={(e) => updateFilters({ fileType: e.target.value })} aria-label="File type">
                    <option value="">All file types</option>
                    {options.fileTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <select value={filters.status} onChange={(e) => updateFilters({ status: e.target.value })} aria-label="Status">
                    <option value="">All statuses</option>
                    {options.statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={hours} onChange={(e) => updateFilters({}, Number(e.target.value))} aria-label="Recent jobs time window">
                    {HOURS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <label className="jobs-filter-checkbox">
                    <input
                        type="checkbox"
                        checked={filters.errorsOnly}
                        onChange={(e) => updateFilters({ errorsOnly: e.target.checked })}
                    />
                    Errors only
                </label>
                {hasFilters && (
                    <button className="jobs-filter-clear-all" onClick={clearFilters}>✕ Clear filters</button>
                )}
            </div>

            {/* Active Jobs */}
            <section className="jobs-section">
                <h2>
                    Active Jobs ({hasFilters ? `${filteredActive.length.toLocaleString()} of ${activeJobs.length.toLocaleString()}` : activeJobs.length.toLocaleString()})
                </h2>
                {filteredActive.length > 0 ? (
                    <div className="jobs-list">
                        {filteredActive.slice(0, activeLimit).map(job => (
                            <div key={job.jobId} className={`job-card ${job.jobStatus?.toLowerCase() || 'running'} ${expandedJobs[job.jobId] ? 'expanded' : ''}`}>
                                <div className="job-card-header" onClick={() => toggleJobExpanded(job.jobId)}>
                                    <span className="job-expand-icon">{expandedJobs[job.jobId] ? '▼' : '▶'}</span>
                                    <span className="job-status-icon">{job.jobStatus === 'Running' ? '🔵' : '⏳'}</span>
                                    <div className="job-card-info">
                                        <span className="job-card-name">{job.jobName}</span>
                                        <span className="job-card-type">{job.jobType}</span>
                                        {job.clientId && <span className="job-card-client">{job.clientId}</span>}
                                    </div>
                                    <span className="job-card-time">Started: {formatTime(job.jobStartTime ?? job.startTime)}</span>
                                </div>

                                {/* Collapsed progress */}
                                {!expandedJobs[job.jobId] && (
                                    <div className="job-card-progress-summary">
                                        {job.currentFileName && (
                                            <div className="job-current-task">
                                                {job.currentFileName}
                                                {job.currentStepName && <span> → {job.currentStepName}</span>}
                                            </div>
                                        )}
                                        {job.stepTotalItems > 0 && (
                                            <div className="job-progress-bar-container">
                                                <div className="job-progress-bar">
                                                    <div className="job-progress-fill" style={{ width: `${job.stepProgressPercent || 0}%` }}></div>
                                                </div>
                                                <span className="job-progress-text">
                                                    {(job.stepProcessedItems || 0).toLocaleString()} / {job.stepTotalItems.toLocaleString()} ({job.stepProgressPercent || 0}%)
                                                </span>
                                            </div>
                                        )}
                                        {job.totalFiles > 0 && (
                                            <div className="job-files-count">Files: {job.processedFiles || 0} / {job.totalFiles}</div>
                                        )}
                                    </div>
                                )}

                                {/* Expanded detail */}
                                {expandedJobs[job.jobId] && jobDetails[job.jobId] && (
                                    <div className="job-card-detail">
                                        {jobDetails[job.jobId].error ? (
                                            <div className="job-no-files">⚠️ Couldn't load job details: {jobDetails[job.jobId].error}</div>
                                        ) : jobDetails[job.jobId].files?.length > 0 ? (
                                            <div className="job-files-tree">
                                                {jobDetails[job.jobId].files.map(file => (
                                                    <div key={file.fileId} className={`job-file-item ${file.status?.toLowerCase()}`}>
                                                        <div className="job-file-header">
                                                            <span className="job-file-status">{getStatusIcon(file.status)}</span>
                                                            <span className="job-file-name">{file.fileName}</span>
                                                            {file.fileType && <span className="job-file-type">[{file.fileType.toUpperCase()}]</span>}
                                                            <span className="job-file-steps-count">
                                                                {file.steps?.filter(s => s.status === 'Complete').length || 0} / {file.steps?.length || 0} steps
                                                            </span>
                                                        </div>
                                                        {file.steps?.length > 0 && (
                                                            <div className="job-steps-list">
                                                                {file.steps.map(step => (
                                                                    <div key={step.stepId} className={`job-step-item ${step.status?.toLowerCase()}`}>
                                                                        <span className="job-step-status">{getStepStatusIcon(step.status)}</span>
                                                                        <span className="job-step-name">{step.stepName}</span>
                                                                        {step.totalItems > 0 && (
                                                                            <span className="job-step-progress">
                                                                                {(step.processedItems || 0).toLocaleString()} / {step.totalItems.toLocaleString()}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="job-no-files">No files recorded</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="jobs-empty">{hasFilters ? 'No active jobs match these filters' : 'No active jobs'}</div>
                )}
                <ShowMore shown={activeLimit} total={filteredActive.length} onMore={() => setActiveLimit(l => l + PAGE_SIZE)} />
            </section>

            {/* Recent Jobs */}
            <section className="jobs-section">
                <h2>
                    Recent Jobs · {HOURS_OPTIONS.find(o => o.value === hours)?.label || `Last ${hours} hours`} ({hasFilters ? `${filteredRecent.length.toLocaleString()} of ${recentJobs.length.toLocaleString()}` : recentJobs.length.toLocaleString()})
                </h2>
                {filteredRecent.length > 0 ? (
                    <div className="jobs-list">
                        {filteredRecent.slice(0, recentLimit).map(job => (
                            <div key={job.jobId} className={`job-card compact ${job.status?.toLowerCase()}`}>
                                <div className="job-card-header" onClick={() => toggleJobExpanded(job.jobId)}>
                                    <span className="job-expand-icon">{expandedJobs[job.jobId] ? '▼' : '▶'}</span>
                                    <span className="job-status-icon">{getStatusIcon(job.status)}</span>
                                    <div className="job-card-info">
                                        <span className="job-card-name">{job.jobName}</span>
                                        <span className="job-card-type">{job.jobType}</span>
                                        {job.clientId && <span className="job-card-client">{job.clientId}</span>}
                                    </div>
                                    {job.totalItems > 0 && (
                                        <span
                                            className={`job-card-count ${(job.processedItems || 0) < job.totalItems ? 'partial' : ''}`}
                                            title="Items processed / total (e.g. notes posted)"
                                        >
                                            {(job.processedItems || 0).toLocaleString()} / {job.totalItems.toLocaleString()}
                                        </span>
                                    )}
                                    <span className="job-card-time">
                                        {formatShortDateTime(job.startTime)}
                                        {job.errorMessage && <span className="job-error-indicator" title={job.errorMessage}>⚠️</span>}
                                    </span>
                                </div>

                                {/* Expanded detail */}
                                {expandedJobs[job.jobId] && jobDetails[job.jobId] && (
                                    <div className="job-card-detail">
                                        {job.errorMessage && (
                                            <div className="job-error-message">{job.errorMessage}</div>
                                        )}
                                        {jobDetails[job.jobId].error ? (
                                            <div className="job-no-files">⚠️ Couldn't load job details: {jobDetails[job.jobId].error}</div>
                                        ) : jobDetails[job.jobId].files?.length > 0 ? (
                                            <div className="job-files-tree">
                                                {jobDetails[job.jobId].files.map(file => (
                                                    <div key={file.fileId} className={`job-file-item ${file.status?.toLowerCase()}`}>
                                                        <div className="job-file-header">
                                                            <span className="job-file-status">{getStatusIcon(file.status)}</span>
                                                            <span className="job-file-name">{file.fileName}</span>
                                                            {file.fileType && <span className="job-file-type">[{file.fileType.toUpperCase()}]</span>}
                                                            {file.recordCount > 0 && (
                                                                <span className="job-file-records" title="Records processed / total">
                                                                    {(file.processedRecords || 0).toLocaleString()} / {file.recordCount.toLocaleString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {file.errorMessage && <div className="job-file-error">{file.errorMessage}</div>}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="job-no-files">No files recorded</div>
                                        )}
                                        <JobErrorLogs logs={jobDetails[job.jobId].logs} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="jobs-empty">{hasFilters ? 'No recent jobs match these filters' : 'No recent jobs'}</div>
                )}
                <ShowMore shown={recentLimit} total={filteredRecent.length} onMore={() => setRecentLimit(l => l + PAGE_SIZE)} />
            </section>
        </div>
    );
}

// Warning/error log lines for a job (e.g. which CaseNoteIDs failed to post)
function JobErrorLogs({ logs }) {
    const problems = (logs || []).filter(l => l.logLevel === 'Error' || l.logLevel === 'Warning');
    if (problems.length === 0) return null;
    return (
        <div className="job-logs">
            {problems.map((l, i) => (
                <div key={i} className={`job-log-item ${l.logLevel.toLowerCase()}`}>
                    <span className="job-log-time">{formatTime(l.timestamp)}</span>
                    <span className="job-log-message">{l.message}</span>
                </div>
            ))}
        </div>
    );
}

function ShowMore({ shown, total, onMore }) {
    if (total <= shown) return null;
    return (
        <div className="jobs-show-more">
            <span>Showing {shown.toLocaleString()} of {total.toLocaleString()}</span>
            <button className="btn btn-ghost" onClick={onMore}>Show {Math.min(PAGE_SIZE, total - shown)} more</button>
        </div>
    );
}

export default Jobs;
